import googleIcon from "../assets/google-icon.svg";
import appleIcon from "../assets/apple-icon.svg";
import facebookIcon from "../assets/facebook-icon.svg";
import microsoftIcon from "../assets/microsoft-icon.svg";
import { Icon } from "@iconify/react";
import AppleSignin from "react-apple-signin-auth";
import { useEffect, type ButtonHTMLAttributes, type ClassAttributes } from "react";
import type { JSX } from "react/jsx-runtime";
import { useForm } from "react-hook-form";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import Cookies from "js-cookie";

import Input, { InputPassword } from "../components/input";
import { LoginBox } from "../components/loginBox";
import { Link } from "../components/link";
import userTimeZoneCountryCode from "../components/userTimeZoneCountryCode";
import CookieRedirect from "../components/cookieRedirect";
import axios, { AxiosError, type AxiosResponse } from "axios";
import type { components as sso } from "../hooks/sso";
import userBrowserLanguage from "../components/userBrowserLanguage";
import { useStateMachine } from "little-state-machine";
import { updateState } from "../littleStateMachine/updateRedirectUrl";
import getTranslationJson from "../components/getTranslationJson";
import { Button, SocialButton } from "../components/button";
import { isNotLocalUrl } from "../littleStateMachine/utils";
import { useSearchParams } from "react-router";
import DeveloperSettings from "../components/developerSettings";

interface FormValues {
  username: string;
  password: string;
  apiError?: string;
}

interface LoginPayload {
  username: string;
  password: string;
  apiError?: string;
  country_code?: string;
}

function AuthLogin() {
  const { actions, state, getState } = useStateMachine({
    actions: { updateState },
  });
  const [searchParams] = useSearchParams();

  const cookie_domain =
    searchParams.has("cookie_domain") && import.meta.env.VITE_PUBLIC_DEV === "true"
      ? searchParams.get("cookie_domain") || ""
      : import.meta.env.VITE_PUBLIC_SUNSTAR_GUM_COOKIE_DOMAIN;
  // Get the previous page (if any)
  const origin_url = searchParams.has("redirect_url")
    ? searchParams.get("redirect_url") || ""
    : document.referrer || import.meta.env.VITE_PUBLIC_SUNSTAR_GUM_URL;
  const countryCode = userTimeZoneCountryCode();
  const socialLoginEnabled = Boolean(state.developerSettings?.socialLoginEnabled);
  // Access the client
  const queryClient = useQueryClient();

  const translation = getTranslationJson(userBrowserLanguage());

  useEffect(() => {
    if (isNotLocalUrl(origin_url)) {
      actions.updateState({
        loginPageData: { redirectUrl: origin_url, cookieDomain: cookie_domain },
      });
    }
  }, []);
  // // Queries
  // const query = useQuery({
  //   queryKey: ["todos"],
  //   queryFn: () =>
  //     fetch("https://api.github.com/repos/TanStack/query").then((res) =>
  //       res.json()
  //     ),
  // });

  // Mutations
  const mutationLogin = useMutation({
    mutationKey: ["login"],
    mutationFn: async (formData: FormValues | LoginPayload): Promise<AxiosResponse<sso["schemas"]["Token"]>> => {
      return axios.post(import.meta.env.VITE_PUBLIC_PLATFORM_SSO_API_URL! + "/login", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        params: {
          app_id: "GUMWebsite",
        },
      });
    },
    onSuccess: (response: AxiosResponse<sso["schemas"]["Token"]>) => {
      const data = response.data;
      // Invalidate and refetch
      queryClient.invalidateQueries({ queryKey: ["login"] });
      //  set the cookie
      Cookies.set(import.meta.env.VITE_PUBLIC_SUNSTAR_GUM_COOKIE_NAME!, data.access_token, {
        path: "/",
        domain: getState().loginPageData.cookieDomain,
      });
      // console.log("Origin url:", origin_url);
      // console.log("getState url:", getState().loginPageData.redirectUrl);
      const redirect_url = getState().loginPageData.redirectUrl;

      // redirect in Deaplink case
      if (
        redirect_url.startsWith(import.meta.env.VITE_PUBLIC_UNITY_DEEPLINK_PREFIX_GUMPLAYBRUSH) ||
        redirect_url.startsWith(import.meta.env.VITE_PUBLIC_UNITY_DEEPLINK_PREFIX_GUMSMART)
      ) {
        // adding event listeners to detect if the redirect was successfully or not
        let appOpened = false;
        const onHidden = () => {
          appOpened = true;
        };
        document.addEventListener("visibilitychange", onHidden);
        window.addEventListener("pagehide", onHidden);

        //redirect with deeplink querystring parameters
        // window.location.href =
        //   redirect_url +
        //   (redirect_url.includes("?") ? "&" : "?") +
        //   "access_token=" +
        //   data.access_token +
        //   "&" +
        //   "refresh_token=" +
        //   data.refresh_token; // redirect to the original URL with token as query param

        //redirect with deeplink parameters in the format expected by the app
        const jsonData = JSON.stringify(data);
        window.location.href = redirect_url + "" + jsonData;

        setTimeout(() => {
          document.removeEventListener("visibilitychange", onHidden);
          window.removeEventListener("pagehide", onHidden);

          if (!appOpened && document.visibilityState === "visible") {
            // show "app not installed" message or fallback URL
            setError("apiError", {
              type: "custom",
              message:
                translation.error_redirect_to_app ||
                "Failed to redirect to the app. Please make sure you have the latest version of the app installed.",
            });
          }
        }, 1500);
      }
      window.location.href = redirect_url; // redirect to the original URL
    },
    onError: (data: AxiosError) => {
      // Invalidate and refetch
      console.error("Login error:", data);
      // queryClient.invalidateQueries({ queryKey: ["login"] });
      if (data.status === 404) {
        setError("apiError", {
          type: "custom",
          message:
            translation.error_account_not_found ||
            "Sorry, we couldn't find your account. Please check that your email address is spelled correctly.",
        });
      } else if (data.status === 401) {
        setError("apiError", {
          type: "custom",
          message:
            translation.error_wrong_password ||
            "Sorry, your password was incorrect. Please double-check your password.",
        });
      } else if (data.status === 403) {
        setError("apiError", {
          type: "custom",
          message:
            translation.error_account_not_confirmed ||
            "Please verify your email address using the link we sent you. Before you try to login",
        });
      } else {
        setError("apiError", {
          type: "custom",
          message: data.message || "An error occurred while logging in.",
        });
      }
    },
  });

  const {
    register,
    handleSubmit,
    // control,
    formState: { errors },
    // getValues,
    // setValue,
    setError,
    clearErrors,
  } = useForm<FormValues>();

  const loginHandleSubmit = (form_data: LoginPayload) => {
    if (import.meta.env.VITE_PUBLIC_DEV === "true") {
      console.log(form_data);
      console.log("Time Zone Country Code:", countryCode);
      console.log("Browser Language:", userBrowserLanguage());
    }
    clearErrors(["apiError", "username", "password"]);
    mutationLogin.mutate({ ...form_data, country_code: countryCode });
  };

  return (
    <div className="font-inter-24">
      <DeveloperSettings />
      <CookieRedirect />
      <LoginBox>
        <header className="flex flex-col mb-6 items-center justify-center">
          {/* <h1 className="mb-2 mt-2 text-xl sm:text-3xl text-slate-900 font-semibold text-center"> */}
          {/* <h1 className="mb-4 mt-4 text-xl sm:text-5xl text-slate-900 font-bold text-center"> */}
          {/* <Header1>{translation.login_title || "Login"}</Header1> */}
          {/* <P>
            {translation.login_description || "Sign in to your account to access all the features of this website."}
          </P> */}
          {/* {import.meta.env.VITE_PUBLIC_DEV=== "true" && <P>{state.loginPageData.redirectUrl}</P>} */}
        </header>
        <form onSubmit={handleSubmit(loginHandleSubmit)}>
          <div className="flex flex-col gap-5">
            <input type="hidden" {...register("apiError")} />
            <Input
              {...register("username", { required: true })}
              type="email"
              label={translation.field_email_label || "Email"}
              placeholder={translation.field_email_placeholder || "ex: john_doe or john_doe@domain.com"}
              name="username"
            />
            <InputPassword
              {...register("password", { required: true })}
              type="text"
              label={translation.field_password_label || "Password"}
              placeholder={translation.field_password_placeholder || "*************"}
              name="password"
            />
            <div className="flex justify-end items-center align-end">
              <Link href="/auth/forgot-password">{translation.forgot_your_password || "Forgot your password?"}</Link>
            </div>
            <Button type="submit" disabled={mutationLogin.isPending}>
              <Icon
                icon={mutationLogin.isPending ? "prime:spinner" : "prime:sign-in"}
                className={mutationLogin.isPending ? "text-2xl mr-1 animate-spin" : "text-3xl mr-1 fill-white"}
              />
              {mutationLogin.isPending
                ? `${translation.button_text_sign_in || "Sign In"}...`
                : translation.button_text_sign_in || "Sign In"}
            </Button>
          </div>

          <div className="mb-2 text-center">
            {errors.apiError && (
              <div className="mt-5 text-sm text-rose-600 bg-rose-50 py-3 px-4 rounded-md font-medium">
                {typeof errors.apiError.message === "string"
                  ? errors.apiError.message
                  : "An error occurred while logging in."}
              </div>
            )}
            {errors.username && (
              <div className="mt-5 text-sm text-rose-600 bg-rose-50 py-3 px-4 rounded-md font-medium">
                {translation.error_required_field || "Required field"}
              </div>
            )}
          </div>
        </form>
        <div className={socialLoginEnabled ? "SocialLogin" : "SocialLogin hidden"}>
          <div className="flex flex-col gap-2">
            <div className="h-[1px] bg-gray-300 relative my-5">
              <p className="text-slate-700 text-sm py-1 px-3 absolute whitespace-nowrap bg-white top-[-14px] left-1/2 transform -translate-x-1/2 ">
                {translation.or_login_with || "or"}
              </p>
            </div>
            <div className="flex flex-col gap-2">
              <form
                name="google-login-form"
                method="get"
                action={import.meta.env.VITE_PUBLIC_PLATFORM_SSO_API_URL! + "/sso_google/login"}
              >
                <input type="hidden" name="origin_url" value={state.loginPageData.redirectUrl} />
                <SocialButton type="submit" icon={googleIcon}>
                  {translation.button_google_sign_in || "Sign in with Google"}
                </SocialButton>
              </form>
              <AppleSignin
                /** Auth options passed to AppleID.auth.init() */
                authOptions={{
                  /** Client ID - eg: 'com.example.com' */
                  clientId: import.meta.env.VITE_PUBLIC_SSO_APPLE_CLIENT_ID!,
                  /** Requested scopes, seperated by spaces - eg: 'email name' */
                  scope: "email name",
                  /** Apple's redirectURI - must be one of the URIs you added to the serviceID - the undocumented trick in apple docs is that you should call auth from a page that is listed as a redirectURI, localhost fails */
                  redirectURI: import.meta.env.VITE_PUBLIC_PLATFORM_SSO_API_URL! + "/sso_apple/callback",
                  /** State string that is returned with the apple response */
                  state: origin_url,
                  /** Nonce */
                  nonce: "nonce",
                  /** Uses popup auth instead of redirection */
                  usePopup: false,
                }} // REQUIRED
                /** General props */
                uiType="dark"
                /** className */
                className="apple-auth-btn"
                /** Removes default style tag */
                noDefaultStyle={false}
                /** Allows to change the button's children, eg: for changing the button text */
                buttonExtraChildren="Continue with Apple"
                /** Extra controlling props */
                /** Called upon signin success in case authOptions.usePopup = true -- which means auth is handled client side */
                onSuccess={(response: unknown) => console.log(response)} // default = undefined
                /** Called upon signin error */
                onError={(error: unknown) => console.error(error)} // default = undefined
                /** Skips loading the apple script if true */
                skipScript={false} // default = undefined
                /** Apple image props */
                iconProps={{ style: { marginTop: "0px" } }} // default = undefined
                /** render function - called with all props - can be used to fully customize the UI by rendering your own component  */
                render={(
                  props: JSX.IntrinsicAttributes &
                    ClassAttributes<HTMLButtonElement> &
                    ButtonHTMLAttributes<HTMLButtonElement>,
                ) => (
                  <SocialButton icon={appleIcon} {...props}>
                    {translation.button_apple_sign_in || "Sign in with Apple"}
                  </SocialButton>

                  // <button
                  //   {...props}
                  //   className="cursor-pointer items-center justify-center gap-x-2 font-semibold disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 ease-in-out bg-white text-gray-900 shadow-sm shadow-gray-500/5 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 py-0 px-5 h-11 rounded-md inline-flex"
                  // >
                  //   <div className="flex items-center justify-center">
                  //     <img alt="apple icon" src={appleIcon} width={24} />
                  //   </div>
                  //   <div className="flex items-center justify-center  min-w-45">
                  //     {translation.button_apple_sign_in || "Sign in with Apple"}
                  //   </div>
                  // </button>
                )}
              />

              <form
                name="microsoft-login-form"
                method="get"
                action={import.meta.env.VITE_PUBLIC_PLATFORM_SSO_API_URL! + "/sso_facebook/login"}
              >
                <input type="hidden" name="origin_url" value={state.loginPageData.redirectUrl} />
                <SocialButton type="submit" icon={facebookIcon}>
                  {translation.button_facebook_sign_in || "Sign in with Facebook"}
                </SocialButton>
              </form>
              <form
                name="microsoft-login-form"
                method="get"
                action={import.meta.env.VITE_PUBLIC_PLATFORM_SSO_API_URL! + "/sso_microsoft/login"}
              >
                <input type="hidden" name="origin_url" value={state.loginPageData.redirectUrl} />
                <SocialButton type="submit" icon={microsoftIcon}>
                  {translation.button_microsoft_sign_in || "Sign in with Microsoft"}
                </SocialButton>
              </form>
            </div>
          </div>
        </div>
        <footer className="text-center text-sm text-slate-600 mt-7">
          {translation.dont_have_an_account || "Don't have an account?"}
          <Link href="/auth/register">{translation.sign_up_here || "Sign up here"}</Link>
        </footer>
      </LoginBox>
    </div>
  );
}

export default AuthLogin;
